import { useTheme } from '@automata/mui-theme';
import { useSvgContext } from '@automata/ui/src/Svg';
import { ScaleBand, ScaleLinear, ScaleOrdinal } from 'd3-scale';

import {
  DelayedTaskTimeIcon,
  ExtendedTaskTimeIcon,
  ReducedTaskTimeIcon,
} from './BarIcons';
import LabwareViewTooltip from './LabwareViewTooltip';
import { Bar, BarProps } from '../../components/Bar';
import ReplanTaskDurationTooltip, {
  Props as ReplanTaskDurationTooltipProps,
} from '../../components/ReplanTaskDurationTooltip';
import useTooltipContext from '../../components/Tooltip/useTooltipContext';
import { RunLabware } from '../../types';

export type Props = {
  labwareData: RunLabware;
  scaleX: ScaleLinear<number, number, never>;
  scaleY: ScaleBand<string>;
  scaleColor: ScaleOrdinal<string, string, never>;
  isBrush?: boolean;
};

const BAR_VERTICAL_GAP = 2; // Gap between bars in the same labware row

const Bars = ({
  labwareData,
  scaleColor,
  scaleX,
  scaleY,
  isBrush = false,
}: Props): JSX.Element => {
  const { updateTooltip } = useTooltipContext();
  const { height, width } = useSvgContext();
  const theme = useTheme();

  return (
    <>
      {scaleY.domain().map((labwareId) => {
        const labware = labwareData[labwareId];

        if (labware === undefined) return null;

        const maxLevel = Math.max(...labware.tasks.map((t) => t.level ?? 0), 0);

        return labware.tasks.map((taskProgress) => {
          const barPosXStart = scaleX(taskProgress.duration.start);
          const barPosXEnd = scaleX(taskProgress.duration.end);
          const level = taskProgress.level ?? 0;
          const baseY = scaleY(labwareId) ?? 0;
          const barHeight = scaleY.bandwidth() / (maxLevel + 1);
          const barGap = BAR_VERTICAL_GAP;
          const barPosY = baseY + level * (barHeight + barGap);
          const barWidth = barPosXEnd - barPosXStart;
          const currentDuration = taskProgress.duration;
          const previousDuration = taskProgress.previousDuration;

          const replanTooltipDefaultProps: Omit<
            ReplanTaskDurationTooltipProps,
            'variant'
          > = {
            currentDuration,
            previousDuration,
            svgWidth: width,
            svgHeight: height,
          };

          const tooltip = (
            <LabwareViewTooltip
              maestroTaskProgress={taskProgress}
              maestroTaskLabware={labware}
              svgWidth={width}
              svgHeight={height}
            />
          );
          const isTaskIncomplete =
            taskProgress.status === 'pending' ||
            taskProgress.status === 'in_progress';

          const conditionalProps: Partial<BarProps> = (() => {
            if (isBrush) {
              return {
                'data-testid': `bar-brush-${labwareId}-${taskProgress.task.id}`,
                fillOpacity: isTaskIncomplete ? 0.2 : 1,
                strokeWidth: 1,
                strokeOpacity: 0.5,
                rx: 1,
              };
            }

            const isTaskTimeReduced =
              taskProgress.previousDuration !== undefined &&
              taskProgress.previousDuration.end -
                taskProgress.previousDuration.start >
                taskProgress.duration.end + taskProgress.duration.start;

            const isTaskTimeExtended =
              taskProgress.previousDuration !== undefined &&
              taskProgress.previousDuration.end -
                taskProgress.previousDuration.start <
                taskProgress.duration.end - taskProgress.duration.start;

            const isTaskDelayed =
              taskProgress.previousDuration !== undefined &&
              taskProgress.duration.start > taskProgress.previousDuration.start;

            let icon = undefined;
            let iconAriaLabel = undefined;
            let replanTooltip: JSX.Element | undefined = undefined;

            if (isTaskTimeReduced) {
              icon = ReducedTaskTimeIcon;
              iconAriaLabel = 'Time reduced';
              replanTooltip = (
                <ReplanTaskDurationTooltip
                  {...replanTooltipDefaultProps}
                  variant="reduced"
                />
              );
            }

            if (isTaskTimeExtended) {
              icon = ExtendedTaskTimeIcon;
              iconAriaLabel = 'Time extended';
              replanTooltip = (
                <ReplanTaskDurationTooltip
                  {...replanTooltipDefaultProps}
                  variant="extended"
                />
              );
            }

            if (isTaskDelayed) {
              icon = DelayedTaskTimeIcon;
              iconAriaLabel = 'Delayed';
              replanTooltip = (
                <ReplanTaskDurationTooltip
                  {...replanTooltipDefaultProps}
                  variant="delayed"
                />
              );
            }

            return {
              'data-testid': `bar-view-${labwareId}-${taskProgress.task.id}`,
              text: taskProgress.task.label,
              icon,
              iconAriaLabel,
              iconOnClick: () => {
                updateTooltip({
                  tooltipOpen: true,
                  tooltipTop: barPosY + barHeight / 2,
                  tooltipLeft: barPosXStart + barWidth / 2,
                  tooltipData: replanTooltip,
                });
              },
              onMouseOut: () => {
                updateTooltip({ tooltipOpen: false });
              },
              onMouseOver: () => {
                updateTooltip({
                  tooltipOpen: true,
                  tooltipTop: barPosY + barHeight / 2,
                  tooltipLeft: barPosXStart + barWidth / 2,
                  tooltipData: tooltip,
                });
              },
              fillOpacity: isTaskIncomplete ? 0.3 : 1,
              strokeDasharray: isTaskIncomplete ? '5,2' : undefined,
              textFill: isTaskIncomplete
                ? theme.palette.text.primary
                : undefined,
              rx: 2,
            };
          })();

          return (
            <Bar
              key={taskProgress.task.id}
              x={barPosXStart}
              y={barPosY}
              width={barWidth}
              height={barHeight}
              fill={scaleColor(taskProgress.task.instrument.id)}
              {...conditionalProps}
            />
          );
        });
      })}
    </>
  );
};

export default Bars;
